import axios, { type Canceler } from 'axios';
import { showLogin, isAuthDisabled } from '@/utils/login';

export interface ApiResponse<T = any> {
  data: T;
  code: string;
  msg: string;
}

export class AxiosMode {
  static get IS_DEV() {
    return import.meta.env.DEV;
  }
  /** DEV 与 no-login 本地预览共用 Vite proxy，避免构建后 API_HOST 变成空前缀导致请求打到错误源 */
  static get USE_LOCAL_PROXY() {
    return AxiosMode.IS_DEV || import.meta.env.VITE_DISABLE_AUTH === 'true';
  }
  static get SERVE_HOST() {
    return `https://${import.meta.env.VITE_WEB_DOMAIN}`;
  }
  static get FILES_HOST() {
    return `https://${import.meta.env.VITE_FILES_DOMAIN}`;
  }
  static get API_HOST() {
    // 开发 / 免登录本地预览走 Vite proxy，线上通过 Nginx proxy 解决跨域
    if (AxiosMode.USE_LOCAL_PROXY) {
      return '/interface';
    }
    return '';
  }

  /** 素材库 API 地址（animiz.cn 可商用音乐） */
  static get MATERIAL_API_HOST() {
    if (AxiosMode.USE_LOCAL_PROXY) {
      return '/materialapi';
    }
    return 'https://www.animiz.cn/materialapi';
  }

  /** TTS 配音 API 地址 */
  static get TTS_API_HOST() {
    if (AxiosMode.USE_LOCAL_PROXY) {
      return '/ttsapi';
    }
    return 'https://task.wancaiinfo.com';
  }

  get({ axiosRequestConfig = {}, remotePath = '' }) {
    let cancelFn = () => {};
    const reqFn = axios.get(remotePath, {
      ...axiosRequestConfig,
      cancelToken: new axios.CancelToken((canceler: Canceler) => {
        cancelFn = canceler;
      })
    });
    return { req: reqFn, cancel: cancelFn };
  }

  post({
    postData = {},
    axiosRequestConfig = {},
    remotePath = ''
  }: {
    postData?: any;
    axiosRequestConfig?: any;
    remotePath: string;
  }) {
    let cancelFn = () => {};
    const reqFn = new Promise((resolve, reject) => {
      axios
        .post(remotePath, postData, {
          headers: {},
          ...axiosRequestConfig,
          cancelToken: new axios.CancelToken((canceler: Canceler) => {
            cancelFn = canceler;
          })
        })
        .then((res) => {
          const { code, msg, data } = res.data;
          const apiRes: ApiResponse = { ...res.data, code, msg, data };
          const errorMsg = 'RequestMsg.UnexpectedErrorRetry';
          switch (code) {
            case 'LOGIN_SIGNOUT':
            case 'LOGIN_TIMEOUT':
              if (isAuthDisabled()) {
                resolve(apiRes);
                break;
              }

              showLogin();
              reject({ ...res.data, errorMsg: msg || errorMsg });
              break;
            case 'OK':
              resolve(apiRes);
              break;
            default:
              reject({ ...res.data, errorMsg: msg || errorMsg });
          }
        })
        .catch((err: any) => {
          reject(err);
        });
    });
    return { req: reqFn, cancel: cancelFn };
  }
}
